import { ChevronDown, Search } from 'lucide-react';
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cn } from '../../lib/utils.ts';
import { useDebounced } from '../record-table/use-debounced.ts';
import { nativeSelectClass } from '../ui/input.tsx';

/** As the task filters: the search narrows from the 3rd character, 300 ms after typing stops. */
const SEARCH_MIN_CHARS = 3;
const PANEL_MAX_PX = 288;

export const pickerRowClass =
  'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-(--v2-dropdown-bg-hover)';

/**
 * The companion's select: a trigger and an inline panel with a search field over a list that
 * scrolls inside it. Not a Radix popover: in a Picture-in-Picture window those portal to the main
 * tab's body. It opens upward when there is more room above inside the area that scrolls it, and
 * closes on a pick, Escape, a click outside it or focus leaving it. The arrows (and Home/End) move
 * between the search field and the list's buttons, Enter picks. `children(query, close)` draws the
 * list; `query` is lower-cased and empty until the search applies.
 */
export function InlinePicker({
  id,
  name,
  display,
  searchLabel,
  align = 'start',
  testId,
  children,
}: {
  id?: string;
  /** The trigger's accessible name ("Assignee"). */
  name: string;
  display: ReactNode;
  searchLabel: string;
  /** `end` lines the panel up with the trigger's right edge (a field in the last column). */
  align?: 'start' | 'end';
  testId?: string;
  children: (query: string, close: () => void) => ReactNode;
}) {
  const [placement, setPlacement] = useState<{ up: boolean; maxHeight: number } | null>(null);
  const [typed, setTyped] = useState('');
  const debounced = useDebounced(typed.trim().toLowerCase(), 300);
  const query = debounced.length >= SEARCH_MIN_CHARS ? debounced : '';
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const step = (e: KeyboardEvent<HTMLDivElement>) => {
    const inSearch = e.target === search.current;
    if (!['ArrowDown', 'ArrowUp'].includes(e.key) && (inSearch || !['Home', 'End'].includes(e.key)))
      return;
    const items = [...(list.current?.querySelectorAll<HTMLElement>('button:not(:disabled)') ?? [])];
    if (items.length === 0) return;
    e.preventDefault();
    const at = inSearch ? -1 : items.indexOf(e.target as HTMLElement);
    const next =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? items.length - 1
          : e.key === 'ArrowDown'
            ? Math.min(at + 1, items.length - 1)
            : at - 1;
    // Up from the first row goes back to the search field.
    (next < 0 ? search.current : items[next])?.focus();
  };
  const open = placement !== null;
  const shut = () => {
    setPlacement(null);
    setTyped('');
  };
  const close = () => {
    shut();
    trigger.current?.focus();
  };
  const show = () => {
    const el = trigger.current;
    if (!el) return;
    // The element's own window: in the Picture-in-Picture window that is not `window`.
    const win = el.ownerDocument.defaultView!;
    // The area that clips the panel: its nearest scrolling ancestor, else the window.
    let box = el.parentElement;
    while (box && !/(auto|scroll)/.test(win.getComputedStyle(box).overflowY))
      box = box.parentElement;
    const bounds = box?.getBoundingClientRect() ?? { top: 0, bottom: win.innerHeight };
    const rect = el.getBoundingClientRect();
    const below = bounds.bottom - rect.bottom;
    const above = rect.top - bounds.top;
    const up = below < PANEL_MAX_PX && above > below;
    // Upward it must fit (nothing scrolls above the top); downward the area scrolls to it.
    setPlacement({
      up,
      maxHeight: up
        ? Math.min(PANEL_MAX_PX, above - 8)
        : Math.max(160, Math.min(PANEL_MAX_PX, below - 8)),
    });
  };
  useEffect(() => {
    if (!open) return;
    const doc = wrap.current?.ownerDocument;
    const away = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) shut();
    };
    doc?.addEventListener('mousedown', away);
    return () => doc?.removeEventListener('mousedown', away);
  }, [open]);
  const panelStyle: CSSProperties | undefined = placement
    ? { maxHeight: placement.maxHeight }
    : undefined;
  return (
    <div
      ref={wrap}
      className="relative min-w-0 flex-1"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation();
          close();
        }
      }}
      // Tabbing away closes it; a click elsewhere is the mousedown above (it may move no focus).
      onBlur={(e) => {
        if (open && e.relatedTarget && !wrap.current?.contains(e.relatedTarget)) shut();
      }}
    >
      <button
        ref={trigger}
        id={id}
        type="button"
        aria-label={name}
        aria-expanded={open}
        aria-haspopup="true"
        className={cn(nativeSelectClass, 'flex items-center justify-between gap-2 pr-2 text-left')}
        onClick={() => (open ? shut() : show())}
      >
        <span className="min-w-0 truncate">{display}</span>
        <ChevronDown className="size-4 shrink-0 opacity-70" aria-hidden />
      </button>
      {placement && (
        <div
          className={cn(
            'bg-card absolute z-20 flex w-full min-w-56 flex-col rounded-md border p-1.5 shadow-md',
            placement.up ? 'bottom-full mb-1' : 'top-full mt-1',
            align === 'end' ? 'right-0' : 'left-0',
          )}
          style={panelStyle}
          data-testid={testId}
          onKeyDown={step}
        >
          <label className="border-border mb-1 flex shrink-0 items-center gap-1.5 border-b px-2 pb-1.5">
            <Search className="text-muted-foreground size-3.5 shrink-0" aria-hidden />
            <input
              ref={search}
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Search…"
              aria-label={searchLabel}
              className="placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent text-sm outline-none"
            />
          </label>
          <div ref={list} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {children(query, close)}
          </div>
        </div>
      )}
    </div>
  );
}

/** A flat list of options for `InlinePicker`, narrowed by its query. */
export function PickerOptions<V extends string | null>({
  label,
  options,
  selected,
  query,
  onPick,
}: {
  label: string;
  options: ReadonlyArray<{ value: V; label: string }>;
  selected: V | null;
  query: string;
  onPick: (value: V) => void;
}) {
  const shown = options.filter((o) => !query || o.label.toLowerCase().includes(query));
  if (shown.length === 0)
    return <p className="text-muted-foreground px-2 py-1.5 text-sm">No match.</p>;
  return (
    <ul aria-label={label}>
      {shown.map((o) => (
        <li key={o.value ?? ''}>
          <button
            type="button"
            className={cn(pickerRowClass, o.value === selected && 'font-medium')}
            aria-current={o.value === selected || undefined}
            title={o.label}
            onClick={() => onPick(o.value)}
          >
            <span className="min-w-0 truncate">{o.label}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}
